import { CalendarIcon, XCircle } from 'lucide-react';
import { useState } from 'react';
import type { Matcher } from 'react-day-picker';
import { useTranslation } from 'react-i18next';
import { Button } from '@/components/ui/button';
import { Calendar } from '@/components/ui/calendar';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import {
  Popover,
  PopoverContent,
  PopoverTrigger,
} from '@/components/ui/popover';
import { formatDate } from '@/lib/format';
import { cn } from '@/lib/utils';

type DatePickerProps = {
  label: string;
  placeholder?: string;
  value: Date | undefined;
  onChange: (date: Date | undefined) => void;
  disabled?: Matcher;
};

export function DatePicker({
  label,
  placeholder,
  value,
  onChange,
  disabled,
}: DatePickerProps) {
  const [open, setOpen] = useState(false);
  const { t, i18n } = useTranslation('common');
  const locale = i18n.language ?? 'fr';

  const displayValue = value
    ? formatDate(value, {
        day: '2-digit',
        locale,
        month: '2-digit',
        year: 'numeric',
      })
    : '';

  return (
    <div className='flex flex-col gap-1.5'>
      <Label className='font-normal text-muted-foreground text-sm'>
        {label}
      </Label>
      <Popover onOpenChange={setOpen} open={open}>
        <PopoverTrigger asChild>
          <div
            className={cn(
              'flex h-9 w-full cursor-pointer items-center rounded-md border border-input bg-transparent shadow-xs',
              'focus-within:border-ring focus-within:ring-[3px] focus-within:ring-ring/50',
            )}
          >
            <Input
              className='cursor-pointer border-0 bg-transparent shadow-none focus-visible:ring-0'
              placeholder={placeholder}
              readOnly
              value={displayValue}
            />
            <Button
              aria-label={label}
              className='shrink-0 rounded-l-none border-0 bg-transparent px-3 hover:bg-muted/50'
              size='icon'
              type='button'
              variant='ghost'
            >
              <CalendarIcon className='size-4 text-muted-foreground' />
            </Button>
          </div>
        </PopoverTrigger>
        <PopoverContent align='start' className='w-auto p-0'>
          <Calendar
            captionLayout='dropdown'
            disabled={disabled}
            mode='single'
            onSelect={(date) => {
              onChange(date);
              setOpen(false);
            }}
            selected={value}
          />
          {value ? (
            <div className='border-border border-t p-2'>
              <Button
                aria-label={t('clearDate')}
                className='w-full justify-start gap-2 text-muted-foreground'
                onClick={() => {
                  onChange(undefined);
                  setOpen(false);
                }}
                size='sm'
                type='button'
                variant='ghost'
              >
                <XCircle className='size-4' />
                {t('clearDate')}
              </Button>
            </div>
          ) : null}
        </PopoverContent>
      </Popover>
    </div>
  );
}
